import { openDB } from 'idb'

const DB_NAME = 'wild-train-db'
const DB_VERSION = 6 // v6: Add workout templates stores

let db = null

/**
 * Initialize IndexedDB
 */
export async function initDB() {
  db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      // Create workouts store
      if (!db.objectStoreNames.contains('workouts')) {
        const workoutStore = db.createObjectStore('workouts', {
          keyPath: 'id',
          autoIncrement: false
        })
        workoutStore.createIndex('date', 'date')
        workoutStore.createIndex('type', 'type')
        workoutStore.createIndex('synced', 'synced')
      }

      // Create exercises store
      if (!db.objectStoreNames.contains('exercises')) {
        const exerciseStore = db.createObjectStore('exercises', {
          keyPath: 'id',
          autoIncrement: false
        })
        exerciseStore.createIndex('workout_id', 'workout_id')
      }

      // Create sync queue
      if (!db.objectStoreNames.contains('syncQueue')) {
        db.createObjectStore('syncQueue', {
          keyPath: 'id',
          autoIncrement: true
        })
      }

      // Create exercise library store (v2)
      if (!db.objectStoreNames.contains('exerciseLibrary')) {
        const libraryStore = db.createObjectStore('exerciseLibrary', {
          keyPath: 'id',
          autoIncrement: false
        })
        libraryStore.createIndex('muscleGroup', 'muscleGroup')
        libraryStore.createIndex('name', 'name')
        libraryStore.createIndex('isCustom', 'isCustom')
        libraryStore.createIndex('equipment_type', 'equipment_type')
      }

      // v4: Add equipment_type index if upgrading from v3
      if (oldVersion < 4 && db.objectStoreNames.contains('exerciseLibrary')) {
        const libraryStore = transaction.objectStore('exerciseLibrary')
        if (!libraryStore.indexNames.contains('equipment_type')) {
          libraryStore.createIndex('equipment_type', 'equipment_type')
        }
      }

      // Create weight records store (v3)
      if (!db.objectStoreNames.contains('weightRecords')) {
        const weightStore = db.createObjectStore('weightRecords', {
          keyPath: 'id',
          autoIncrement: false
        })
        weightStore.createIndex('date', 'date')
        weightStore.createIndex('synced', 'synced')
      }

      // Create sync metadata store (v5) for delta sync
      if (!db.objectStoreNames.contains('syncMetadata')) {
        db.createObjectStore('syncMetadata', {
          keyPath: 'entityType'
        })
      }

      // Create workout templates store (v6)
      if (!db.objectStoreNames.contains('workoutTemplates')) {
        const templateStore = db.createObjectStore('workoutTemplates', {
          keyPath: 'id',
          autoIncrement: false
        })
        templateStore.createIndex('user_id', 'user_id')
        templateStore.createIndex('last_used_at', 'last_used_at')
      }

      // Create template exercises store (v6)
      if (!db.objectStoreNames.contains('templateExercises')) {
        const templateExerciseStore = db.createObjectStore('templateExercises', {
          keyPath: 'id',
          autoIncrement: false
        })
        templateExerciseStore.createIndex('template_id', 'template_id')
      }
    }
  })

  return db
}

/**
 * Get database instance
 */
export function getDB() {
  if (!db) {
    throw new Error('Database not initialized. Call initDB() first.')
  }
  return db
}

/**
 * Add or update a workout
 */
export async function saveWorkout(workout) {
  const db = getDB()

  // Generate ID if not exists
  if (!workout.id) {
    workout.id = crypto.randomUUID()
  }

  workout.synced = false
  workout.updatedAt = new Date().toISOString()

  await db.put('workouts', workout)

  // Add to sync queue
  await addToSyncQueue({
    action: 'saveWorkout',
    data: workout,
    timestamp: Date.now()
  })

  return workout
}

/**
 * Get all workouts
 */
export async function getWorkouts(filters = {}) {
  const db = getDB()
  let workouts = await db.getAll('workouts')

  // Apply filters
  if (filters.type) {
    workouts = workouts.filter(w => w.type === filters.type)
  }

  if (filters.startDate) {
    workouts = workouts.filter(w => w.date >= filters.startDate)
  }

  if (filters.endDate) {
    workouts = workouts.filter(w => w.date <= filters.endDate)
  }

  // Sort by date descending
  workouts.sort((a, b) => new Date(b.date) - new Date(a.date))

  return workouts
}

/**
 * Get a single workout by ID
 */
export async function getWorkout(id) {
  const db = getDB()
  return await db.get('workouts', id)
}

/**
 * Delete a workout
 */
export async function deleteWorkout(id) {
  const db = getDB()

  await db.delete('workouts', id)

  // Delete associated exercises
  const exercises = await db.getAllFromIndex('exercises', 'workout_id', id)
  for (const exercise of exercises) {
    await db.delete('exercises', exercise.id)
  }

  // Add to sync queue
  await addToSyncQueue({
    action: 'deleteWorkout',
    data: { id },
    timestamp: Date.now()
  })
}

/**
 * Add item to sync queue
 */
export async function addToSyncQueue(item) {
  const db = getDB()
  await db.add('syncQueue', item)
}

/**
 * Get all pending sync items
 */
export async function getSyncQueue() {
  const db = getDB()
  return await db.getAll('syncQueue')
}

/**
 * Clear sync queue
 */
export async function clearSyncQueue() {
  const db = getDB()
  const tx = db.transaction('syncQueue', 'readwrite')
  await tx.objectStore('syncQueue').clear()
  await tx.done
}

/**
 * Get workout statistics
 */
export async function getWorkoutStats() {
  const workouts = await getWorkouts()

  const now = new Date()
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

  const thisWeek = workouts.filter(w => new Date(w.date) >= weekAgo)

  // Calculate total volume (weight × reps for all sets)
  const totalVolume = await calculateTotalVolume(workouts)

  return {
    totalWorkouts: workouts.length,
    thisWeekWorkouts: thisWeek.length,
    totalDuration: workouts.reduce((sum, w) => sum + (w.duration || 0), 0),
    thisWeekDuration: thisWeek.reduce((sum, w) => sum + (w.duration || 0), 0),
    totalVolume,
    workoutTypes: getWorkoutTypeBreakdown(workouts),
    consecutiveWeeks: calculateConsecutiveWeeks(workouts)
  }
}

/**
 * Calculate total volume (weight × reps) across all workouts
 * Only counts completed sets
 * @param {Array} workouts - Array of workout objects
 * @returns {Promise<number>} - Total volume in kg
 */
async function calculateTotalVolume(workouts) {
  let totalVolume = 0

  for (const workout of workouts) {
    const exercises = await getExercisesByWorkoutId(workout.id)

    for (const exercise of exercises) {
      const migrated = migrateExerciseFormat(exercise)

      if (Array.isArray(migrated.sets)) {
        // New format: sets is an array
        // Only count completed sets
        migrated.sets.forEach(set => {
          if (set.completed) {
            const weight = parseFloat(set.weight) || 0
            const reps = parseInt(set.reps, 10) || 0
            totalVolume += weight * reps
          }
        })
      }
    }
  }

  return totalVolume
}

/**
 * Calculate consecutive weeks with at least one workout
 * Returns the number of consecutive weeks (including current week) with workouts
 */
export function calculateConsecutiveWeeks(workouts) {
  if (!workouts || workouts.length === 0) {
    return 0
  }

  // Get start of current week (Monday)
  const now = new Date()
  const currentWeekStart = getWeekStart(now)

  // Group workouts by week
  const weekSet = new Set()
  workouts.forEach(workout => {
    const workoutDate = new Date(workout.date)
    const weekStart = getWeekStart(workoutDate)
    weekSet.add(weekStart.toISOString())
  })

  // Convert to sorted array of week starts (most recent first)
  const weeks = Array.from(weekSet)
    .map(dateStr => new Date(dateStr))
    .sort((a, b) => b - a)

  // Count consecutive weeks from current week backwards
  let consecutiveCount = 0
  let checkWeek = new Date(currentWeekStart)

  for (let i = 0; i < weeks.length; i++) {
    const weekStart = weeks[i]

    // Check if this week matches our expected consecutive week
    if (weekStart.toISOString() === checkWeek.toISOString()) {
      consecutiveCount++
      // Move to previous week
      checkWeek = new Date(checkWeek)
      checkWeek.setDate(checkWeek.getDate() - 7)
    } else if (weekStart < checkWeek) {
      // We found a gap in consecutive weeks
      break
    }
  }

  return consecutiveCount
}

/**
 * Get the start of the week (Monday) for a given date
 */
function getWeekStart(date) {
  const d = new Date(date)
  const day = d.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const diff = day === 0 ? -6 : 1 - day // Adjust to Monday
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * Get workout type breakdown
 */
function getWorkoutTypeBreakdown(workouts) {
  const breakdown = {}

  workouts.forEach(workout => {
    const type = workout.type || 'other'
    breakdown[type] = (breakdown[type] || 0) + 1
  })

  return breakdown
}

/**
 * Format duration in minutes to human readable format
 * @param {number} minutes - Duration in minutes
 * @returns {string} - Formatted string (e.g., "42h 30m" or "45m")
 */
export function formatDuration(minutes) {
  if (minutes < 60) {
    return `${minutes}m`
  }

  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60

  if (mins === 0) {
    return `${hours}h`
  }

  return `${hours}h ${mins}m`
}

/**
 * Get workouts for a specific month
 * @param {number} year - Year (e.g., 2025)
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} - Object with dates as keys and workout count as values
 */
export async function getWorkoutsByMonth(year, month) {
  const startDate = new Date(year, month - 1, 1).toISOString()
  const endDate = new Date(year, month, 0, 23, 59, 59).toISOString()

  const workouts = await getWorkouts({
    startDate,
    endDate
  })

  // Group by date
  const byDate = {}
  workouts.forEach(workout => {
    const date = new Date(workout.date)
    const day = date.getDate()
    byDate[day] = (byDate[day] || 0) + 1
  })

  return byDate
}

/**
 * Save exercise (add or update)
 */
export async function saveExercise(exercise) {
  const db = getDB()

  // Generate ID if not exists
  if (!exercise.id) {
    exercise.id = crypto.randomUUID()
  }

  await db.put('exercises', exercise)
  return exercise
}

/**
 * Get exercises by workout ID
 */
export async function getExercisesByWorkoutId(workoutId) {
  const db = getDB()
  return await db.getAllFromIndex('exercises', 'workout_id', workoutId)
}

/**
 * Delete exercise by ID
 */
export async function deleteExercise(id) {
  const db = getDB()
  await db.delete('exercises', id)
}

/**
 * Save workout with exercises (transaction)
 */
export async function saveWorkoutWithExercises(workout, exercises = []) {
  const db = getDB()

  // Generate workout ID if not exists
  if (!workout.id) {
    workout.id = crypto.randomUUID()
  }

  workout.synced = false
  workout.updatedAt = new Date().toISOString()
  if (!workout.createdAt) {
    workout.createdAt = workout.updatedAt
  }

  // Save workout
  await db.put('workouts', workout)

  // Save exercises
  for (const exercise of exercises) {
    if (!exercise.id) {
      exercise.id = crypto.randomUUID()
    }
    exercise.workout_id = workout.id
    await db.put('exercises', exercise)
  }

  // Add to sync queue
  await addToSyncQueue({
    action: 'saveWorkout',
    data: { workout, exercises },
    timestamp: Date.now()
  })

  return { workout, exercises }
}

/**
 * Get workout with exercises
 */
export async function getWorkoutWithExercises(workoutId) {
  const workout = await getWorkout(workoutId)
  if (!workout) return null

  const exercises = await getExercisesByWorkoutId(workoutId)

  // Migrate old format exercises to new format
  const migratedExercises = exercises.map(migrateExerciseFormat)

  return { workout, exercises: migratedExercises }
}

/**
 * Migrate exercise from old format to new format
 * Old format: { sets: 4, reps: 10, weight: 20, rest_time: 90 }
 * New format: { sets: [{ weight: 20, reps: 10, completed: true }, ...], rest_time: 90 }
 */
export function migrateExerciseFormat(exercise) {
  // Already in new format
  if (Array.isArray(exercise.sets)) {
    return exercise
  }

  // Convert old format to new format
  const numSets = parseInt(exercise.sets, 10) || 1
  const reps = parseInt(exercise.reps, 10) || 0
  const weight = parseFloat(exercise.weight) || 0

  const setsArray = []
  for (let i = 0; i < numSets; i++) {
    setsArray.push({
      weight: weight,
      reps: reps,
      completed: true // Mark all existing sets as completed
    })
  }

  return {
    ...exercise,
    sets: setsArray
  }
}

/**
 * Create a default set object
 */
export function createDefaultSet(previousSet = null) {
  if (previousSet) {
    return {
      weight: previousSet.weight || 0,
      reps: previousSet.reps || 0,
      completed: false
    }
  }
  return {
    weight: 0,
    reps: 0,
    completed: false
  }
}

// ============================================
// Exercise Library Functions
// ============================================

/**
 * Initialize exercise library with default exercises from cloud
 * NOTE: Default exercises are now managed in defaultExercises.js
 * and imported from Google Sheets on first login
 */
export async function initExerciseLibrary() {
  // This function is now a no-op
  // Default exercises are imported from cloud on first login
  // See initDefaultExercisesFromCloud() below
}

/**
 * Get all exercises from library
 */
export async function getExerciseLibrary(filters = {}) {
  const db = getDB()
  let exercises = await db.getAll('exerciseLibrary')

  // Apply filters
  if (filters.muscleGroup) {
    exercises = exercises.filter(e => e.muscleGroup === filters.muscleGroup)
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase()
    exercises = exercises.filter(e =>
      e.name.toLowerCase().includes(searchLower) ||
      (e.description && e.description.toLowerCase().includes(searchLower))
    )
  }

  if (filters.isCustom !== undefined) {
    exercises = exercises.filter(e => e.isCustom === filters.isCustom)
  }

  // Sort by name
  exercises.sort((a, b) => a.name.localeCompare(b.name, 'zh-TW'))

  return exercises
}

/**
 * Get exercise from library by ID
 */
export async function getExerciseFromLibrary(id) {
  const db = getDB()
  return await db.get('exerciseLibrary', id)
}

/**
 * Save exercise to library
 */
export async function saveExerciseToLibrary(exercise) {
  const db = getDB()

  // Generate ID if not exists
  if (!exercise.id) {
    exercise.id = crypto.randomUUID()
  }

  exercise.isCustom = true
  exercise.synced = false
  exercise.updatedAt = new Date().toISOString()

  if (!exercise.createdAt) {
    exercise.createdAt = exercise.updatedAt
  }

  await db.put('exerciseLibrary', exercise)

  // Add to sync queue
  await addToSyncQueue({
    action: 'saveExerciseLibrary',
    data: exercise,
    timestamp: Date.now()
  })

  return exercise
}

/**
 * Batch save exercises to library (for import or initial setup)
 * @param {Array} exercises - Array of exercise objects
 * @returns {Promise<Array>} - Array of saved exercises
 */
export async function batchSaveExercisesToLibrary(exercises) {
  const db = getDB()
  const saved = []

  for (const exercise of exercises) {
    // Generate ID if not exists
    if (!exercise.id) {
      exercise.id = crypto.randomUUID()
    }

    if (!exercise.createdAt) {
      exercise.createdAt = new Date().toISOString()
    }
    exercise.updatedAt = new Date().toISOString()

    await db.put('exerciseLibrary', exercise)
    saved.push(exercise)
  }

  return saved
}

/**
 * Delete exercise from library
 */
export async function deleteExerciseFromLibrary(id) {
  const db = getDB()

  await db.delete('exerciseLibrary', id)

  // Add to sync queue
  await addToSyncQueue({
    action: 'deleteExerciseLibrary',
    data: { id },
    timestamp: Date.now()
  })
}

/**
 * Get exercises grouped by muscle group
 */
export async function getExercisesByMuscleGroup() {
  const exercises = await getExerciseLibrary()

  const grouped = {
    chest: [],
    back: [],
    shoulders: [],
    arms: [],
    legs: [],
    core: [],
    cardio: [],
    other: []
  }

  exercises.forEach(exercise => {
    const group = exercise.muscleGroup || 'other'
    if (grouped[group]) {
      grouped[group].push(exercise)
    } else {
      grouped.other.push(exercise)
    }
  })

  return grouped
}

/**
 * Get muscle group display name
 */
export function getMuscleGroupName(group) {
  const names = {
    chest: '胸',
    back: '背',
    shoulders: '肩',
    arms: '手臂',
    legs: '腿',
    core: '核心',
    cardio: '有氧',
    other: '其他'
  }
  return names[group] || '其他'
}

/**
 * Get exercise display name (Chinese if available, fallback to English)
 * @param {Object} exercise - Exercise object
 * @returns {string} - Display name
 */
export function getExerciseDisplayName(exercise) {
  if (!exercise) return ''
  return exercise.zhName || exercise.name
}

/**
 * Reset default exercises in local database
 * Deletes all non-custom exercises from IndexedDB
 */
export async function resetDefaultExercisesLocal() {
  const db = getDB()
  const allExercises = await db.getAll('exerciseLibrary')

  // Delete all non-custom exercises
  const defaultExercises = allExercises.filter(e => !e.isCustom)
  for (const exercise of defaultExercises) {
    await db.delete('exerciseLibrary', exercise.id)
  }

  return defaultExercises.length
}

// ============================================
// Weight Records Functions
// ============================================

/**
 * Save weight record (add or update)
 */
export async function saveWeightRecord(record) {
  const db = getDB()

  // Generate ID if not exists
  if (!record.id) {
    record.id = crypto.randomUUID()
  }

  record.synced = false
  record.updatedAt = new Date().toISOString()

  if (!record.createdAt) {
    record.createdAt = record.updatedAt
  }

  await db.put('weightRecords', record)

  // Add to sync queue
  await addToSyncQueue({
    action: 'saveWeightRecord',
    data: record,
    timestamp: Date.now()
  })

  return record
}

/**
 * Get all weight records
 */
export async function getWeightRecords(filters = {}) {
  const db = getDB()
  let records = await db.getAll('weightRecords')

  // Apply filters
  if (filters.startDate) {
    records = records.filter(r => r.date >= filters.startDate)
  }

  if (filters.endDate) {
    records = records.filter(r => r.date <= filters.endDate)
  }

  // Sort by date descending
  records.sort((a, b) => new Date(b.date) - new Date(a.date))

  return records
}

/**
 * Get a single weight record by ID
 */
export async function getWeightRecord(id) {
  const db = getDB()
  return await db.get('weightRecords', id)
}

/**
 * Delete a weight record
 */
export async function deleteWeightRecord(id) {
  const db = getDB()

  await db.delete('weightRecords', id)

  // Add to sync queue
  await addToSyncQueue({
    action: 'deleteWeightRecord',
    data: { id },
    timestamp: Date.now()
  })
}

/**
 * Calculate weight change between current and previous record
 * @returns {{ direction: 'up'|'down'|'stable', diff: number }}
 */
export function calculateWeightChange(currentWeight, previousWeight) {
  if (!previousWeight) {
    return { direction: 'stable', diff: 0 }
  }

  const diff = currentWeight - previousWeight
  const absDiff = Math.abs(diff)

  // Consider change < 0.1kg as stable
  if (absDiff < 0.1) {
    return { direction: 'stable', diff: 0 }
  }

  return {
    direction: diff > 0 ? 'up' : 'down',
    diff: Math.abs(diff)
  }
}

// ============================================
// Delta Sync Metadata Functions
// ============================================

/**
 * Get last sync time for a specific entity type
 * @param {string} entityType - 'workouts' | 'exercises' | 'weightRecords'
 * @returns {Promise<string|null>} - ISO timestamp or null
 */
export async function getLastSyncTime(entityType) {
  const db = getDB()
  const metadata = await db.get('syncMetadata', entityType)
  return metadata?.lastSyncTime || null
}

/**
 * Set last sync time for a specific entity type
 * @param {string} entityType - 'workouts' | 'exercises' | 'weightRecords'
 * @param {string} timestamp - ISO timestamp from server
 */
export async function setLastSyncTime(entityType, timestamp) {
  const db = getDB()
  await db.put('syncMetadata', {
    entityType,
    lastSyncTime: timestamp,
    updatedAt: new Date().toISOString()
  })
}

/**
 * Clear all sync metadata (for reset/logout)
 */
export async function clearSyncMetadata() {
  const db = getDB()
  const tx = db.transaction('syncMetadata', 'readwrite')
  await tx.objectStore('syncMetadata').clear()
  await tx.done
}
